import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";

function FormImporter({ onImported }) {
  const ref = useRef(null);
  const [importing, setImporting] = useState(false);
  const [err, setErr] = useState("");
  const doImport = async (file) => {
    if (!file) return;
    setImporting(true); setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/carrier/templates/import", fd, { timeout: 120000 });
      onImported(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Import failed — try a clearer PDF or image");
    }
    setImporting(false);
  };
  return (
    <div className="mt-2">
      <input ref={ref} type="file" accept=".pdf,image/*" className="hidden" data-testid="form-import-input"
        onChange={e => { doImport(e.target.files?.[0]); e.target.value = ""; }} />
      <button type="button" data-testid="form-import-btn" disabled={importing} onClick={() => ref.current?.click()}
        className="text-xs text-navy hover:underline disabled:opacity-50">
        {importing ? "AI is reading your form…" : "+ Import your existing form (PDF/image) — AI converts it to a template"}
      </button>
      {err && <p className="text-xs text-red-600 mt-1" data-testid="form-import-error">{err}</p>}
    </div>
  );
}

function AttachmentUploader({ inspectionId }) {
  const ref = useRef(null);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("application");
  const [busy, setBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const upload = async (files) => {
    if (!files?.length || !inspectionId) return;
    setBusy(true); setUploadErr("");
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", docType);
      try {
        const res = await api.post(`/inspections/${inspectionId}/attachments`, fd);
        setDocs(d => [...d, res.data]);
      } catch (e) {
        setUploadErr(e.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    setBusy(false);
  };
  return (
    <div className="mb-4 text-left border border-dashed border-slate-200 rounded-lg p-3" data-testid="attachment-uploader">
      <p className="text-xs font-semibold text-slate-500 mb-2">Attach supporting documents (optional)</p>
      <div className="flex items-center gap-2">
        <select value={docType} onChange={e => setDocType(e.target.value)} data-testid="attachment-type-select"
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
          <option value="application">Application</option>
          <option value="coi">COI</option>
          <option value="prior_report">Prior Report</option>
          <option value="other">Other</option>
        </select>
        <input ref={ref} type="file" accept=".pdf,image/*" multiple className="hidden" data-testid="attachment-input"
          onChange={e => { upload(e.target.files); e.target.value = ""; }} />
        <button type="button" data-testid="attachment-upload-btn" disabled={busy} onClick={() => ref.current?.click()}
          className="text-xs px-2.5 py-1.5 bg-navy/10 text-navy rounded-md font-medium hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
          {busy ? "Uploading…" : "Add PDF / Image"}
        </button>
      </div>
      {uploadErr && <p className="text-xs text-red-600 mt-2" data-testid="attachment-error">{uploadErr}</p>}
      {docs.length > 0 && (
        <ul className="mt-2 space-y-1">
          {docs.map(d => (
            <li key={d.id} className="text-xs text-slate-500" data-testid={`attachment-item-${d.id}`}>
              ✓ {d.filename} <span className="text-slate-300">({d.doc_type})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
import {
  ArrowLeft, ClipboardList, Upload, CheckCircle, Copy, ExternalLink,
  AlertCircle, X, MessageSquare, FileText, Sparkles, RefreshCw,
  Link2, Building2
} from "lucide-react";

const INSPECTION_TYPES = [
  { value: "commercial_property", label: "Commercial Property", desc: "Buildings, structures, contents" },
  { value: "workers_comp",        label: "Workers' Comp",       desc: "Workplace safety & operations" },
  { value: "habitational",        label: "Habitational",        desc: "Apartments, condos, HOAs" },
  { value: "general_liability",   label: "General Liability",   desc: "Premises & operations risk" },
  { value: "commercial_auto",     label: "Commercial Auto",     desc: "Fleet vehicles & drivers" },
];

const EMPTY_FORM = {
  insured_name: "", insured_email: "", insured_contact: "",
  broker_email: "", broker_name: "",
  property_address: "", city: "", state: "", policy_number: "",
  due_date: "", notes: "", mode: "ai_chat", template_id: "",
};

export default function OrderInspection() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("manual");
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedTypes, setSelectedTypes] = useState(["commercial_property"]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");

  // CSV state
  const [csvFile, setCsvFile] = useState(null);
  const [csvType, setCsvType] = useState("commercial_property");
  const [csvResult, setCsvResult] = useState(null);

  // AI form drop state
  const [aiFile, setAiFile] = useState(null);
  const [aiParsing, setAiParsing] = useState(false);
  const [aiExtracted, setAiExtracted] = useState(null);
  const [aiConfidence, setAiConfidence] = useState(null);
  const dropRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    api.get("/carrier/templates").then(r => setTemplates(r.data)).catch(() => {});
    api.get("/carrier/business-profiles").then(r => setProfiles(r.data)).catch(() => {});
  }, []);

  const toggleType = (val) => {
    setSelectedTypes(prev =>
      prev.includes(val)
        ? prev.length === 1 ? prev : prev.filter(v => v !== val)
        : [...prev, val]
    );
  };

  // ---------- Submit (manual / AI pre-filled) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTypes.length === 0) { setError("Select at least one inspection type"); return; }
    setError(""); setLoading(true);
    try {
      if (selectedTypes.length === 1) {
        // Single inspection — original endpoint
        const payload = { ...form, inspection_type: selectedTypes[0], business_profile_id: profileId || undefined };
        if (payload.mode !== "form") delete payload.template_id;
        const res = await api.post("/inspections", payload);
        const isForm = res.data.mode === "form";
        const url = `${window.location.origin}/${isForm ? "survey" : "inspection"}/${res.data.access_token}`;
        setResult({ type: "single", ...res.data, inspection_url: url });
      } else {
        // Multi-type order
        const payload = { ...form, inspection_types: selectedTypes, business_profile_id: profileId || undefined };
        if (payload.mode !== "form") delete payload.template_id;
        const res = await api.post("/inspections/multi-order", payload);
        setResult({ type: "multi", ...res.data });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create inspection");
    } finally { setLoading(false); }
  };

  // ---------- CSV upload ----------
  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) { setError("Please select a CSV file"); return; }
    setError(""); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      fd.append("inspection_type", csvType);
      const res = await api.post("/inspections/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "CSV upload failed");
    } finally { setLoading(false); }
  };

  // ---------- AI order form parsing ----------
  const handleAiParse = async () => {
    if (!aiFile) return;
    setAiParsing(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", aiFile);
      const res = await api.post("/inspections/parse-order-form", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ext = res.data.extracted;
      setAiExtracted(ext);
      setAiConfidence(ext.confidence);
      // Pre-fill form
      setForm(prev => ({
        ...prev,
        insured_name:     ext.insured_name     || prev.insured_name,
        insured_email:    ext.insured_email    || prev.insured_email,
        insured_contact:  ext.insured_contact  || prev.insured_contact,
        property_address: ext.property_address || prev.property_address,
        city:             ext.city             || prev.city,
        state:            ext.state            || prev.state,
        policy_number:    ext.policy_number    || prev.policy_number,
        due_date:         ext.due_date         || prev.due_date,
        notes:            ext.notes            || prev.notes,
      }));
      if (ext.inspection_types?.length > 0) {
        const valid = ext.inspection_types.filter(t => INSPECTION_TYPES.some(it => it.value === t));
        if (valid.length > 0) setSelectedTypes(valid);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not parse the order form. Try a clearer image.");
    } finally { setAiParsing(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setAiFile(file);
  };

  const copied = useRef(false);
  const [copiedState, setCopiedState] = useState(false);
  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), 2000);
  };

  // =========== SUCCESS PAGE ===========
  if (result) {
    if (result.type === "multi") {
      return (
        <Layout>
          <div className="max-w-lg mx-auto mt-10 space-y-4 animate-fade-in">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-card text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-1">{result.inspections?.length} Surveys Created!</h2>
              <p className="text-slate-500 text-sm mb-5">The insured received one email with a link to complete all surveys.</p>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4 text-left space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Insured Hub Link (share this)</p>
                <div className="text-xs font-mono text-slate-600 break-all bg-white border border-slate-200 rounded-lg p-2" data-testid="hub-link">
                  {result.hub_url}
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => copyLink(result.hub_url)} data-testid="copy-hub-link"
                    className="flex-1 flex items-center justify-center gap-1.5 h-9 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    <Copy className="w-3.5 h-3.5" /> {copiedState ? "Copied!" : "Copy Hub Link"}
                  </button>
                  <a href={result.hub_url} target="_blank" rel="noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-navy text-white rounded-lg text-xs font-medium hover:bg-navy/90 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" /> Preview Hub
                  </a>
                </div>
              </div>

              <div className="border border-slate-100 rounded-xl divide-y divide-slate-100">
                {result.inspections?.map(insp => {
                  const label = INSPECTION_TYPES.find(t => t.value === insp.inspection_type)?.label || insp.inspection_type;
                  return (
                    <div key={insp.id} className="flex items-center gap-3 px-4 py-3 text-left" data-testid={`result-insp-${insp.inspection_type}`}>
                      <Building2 className="w-4 h-4 text-navy flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700 flex-1">{label}</span>
                      <a href={insp.survey_url} target="_blank" rel="noreferrer" className="text-xs text-navy hover:underline flex items-center gap-1">
                        <Link2 className="w-3 h-3" /> Link
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
            <button onClick={() => { setResult(null); setForm(EMPTY_FORM); setSelectedTypes(["commercial_property"]); setAiExtracted(null); setAiFile(null); }}
              className="w-full text-sm text-slate-400 hover:text-navy transition-colors text-center">
              + Create another order
            </button>
            <button onClick={() => navigate("/dashboard")} className="mt-2 w-full flex items-center justify-center gap-2 text-slate-500 hover:text-navy text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </Layout>
      );
    }

    // Single result
    const url = result.inspection_url;
    return (
      <Layout>
        <div className="max-w-lg mx-auto mt-12 animate-fade-in">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-card">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-2">Survey Created!</h2>
            <p className="text-slate-500 text-sm mb-5">Share this link with the insured.</p>
            {result.email_sent !== undefined && (
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-5 ${result.email_sent ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="email-status">
                {result.email_sent ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                {result.email_sent ? "Invitation email sent" : "Email pending — share link manually"}
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 break-all text-xs text-slate-600 font-mono" data-testid="inspection-link">{url}</div>
            <AttachmentUploader inspectionId={result.id || result.inspection_id} />
            <div className="flex gap-3">
              <button data-testid="copy-link-btn" onClick={() => copyLink(url)}
                className="flex-1 flex items-center justify-center gap-2 h-10 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <Copy className="w-4 h-4" /> {copiedState ? "Copied!" : "Copy Link"}
              </button>
              <a href={url} target="_blank" rel="noreferrer"
                className="flex-1 flex items-center justify-center gap-2 h-10 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors">
                <ExternalLink className="w-4 h-4" /> Preview
              </a>
            </div>
            <button onClick={() => { setResult(null); setForm(EMPTY_FORM); setSelectedTypes(["commercial_property"]); setAiExtracted(null); setAiFile(null); }}
              className="mt-4 text-sm text-slate-400 hover:text-navy transition-colors">Create another inspection</button>
          </div>
          <button onClick={() => navigate("/dashboard")} className="mt-4 w-full flex items-center justify-center gap-2 text-slate-500 hover:text-navy text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  // =========== MAIN FORM ===========
  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="text-slate-500 hover:text-navy transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-800">Order New Survey</h1>
            <p className="text-slate-500 text-sm">Manual entry, AI form drop, or bulk CSV</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          {[
            { key: "manual", label: "Manual Entry",    icon: ClipboardList },
            { key: "ai",     label: "AI Form Drop",    icon: Sparkles },
            { key: "csv",    label: "CSV Upload",      icon: Upload },
          ].map(t => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${tab === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm" data-testid="order-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            <button onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* ── AI FORM DROP TAB ── */}
        {tab === "ai" && (
          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="px-6 py-5">
              <h3 className="font-heading font-semibold text-slate-700 mb-1">AI Order Form Parser</h3>
              <p className="text-sm text-slate-500 mb-5">Drop an existing order form — PDF or image — and AI will extract all fields automatically.</p>

              {!aiExtracted ? (
                <div
                  ref={dropRef}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 ${dragging ? "border-navy bg-navy/5" : "border-slate-300 hover:border-slate-400"}`}
                  data-testid="ai-drop-zone"
                >
                  <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-slate-600 mb-1">Drag & drop your order form here</p>
                  <p className="text-xs text-slate-400 mb-4">PDF, PNG, JPG, or WEBP — max 10MB</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium cursor-pointer transition-colors">
                    <Upload className="w-4 h-4" /> Browse File
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                      onChange={e => setAiFile(e.target.files[0])} data-testid="ai-file-input" />
                  </label>
                  {aiFile && (
                    <p className="text-xs text-navy font-medium mt-3">{aiFile.name}</p>
                  )}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4" data-testid="ai-extracted">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <p className="text-sm font-semibold text-green-800">Form parsed successfully!</p>
                    <span className="ml-auto text-xs text-green-600 font-medium">
                      {Math.round((aiConfidence || 0) * 100)}% confidence
                    </span>
                  </div>
                  <p className="text-xs text-green-700">Fields below have been pre-filled. Review and correct anything before submitting.</p>
                  <button onClick={() => { setAiExtracted(null); setAiFile(null); }}
                    className="mt-2 text-xs text-green-700 hover:underline flex items-center gap-1">
                    <X className="w-3 h-3" /> Clear and try again
                  </button>
                </div>
              )}

              {aiFile && !aiExtracted && (
                <button onClick={handleAiParse} disabled={aiParsing} data-testid="parse-form-btn"
                  className="mt-4 w-full flex items-center justify-center gap-2 h-10 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy/90 transition-colors disabled:opacity-60">
                  {aiParsing ? <><RefreshCw className="w-4 h-4 animate-spin" /> Parsing with AI...</> : <><Sparkles className="w-4 h-4" /> Extract Fields</>}
                </button>
              )}

              {aiExtracted && (
                <p className="text-xs text-slate-500 mt-3 text-center">
                  Switch to <button className="text-navy font-medium hover:underline" onClick={() => setTab("manual")}>Manual Entry</button> to review and submit the pre-filled form.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── MANUAL / REVIEW FORM ── */}
        {(tab === "manual" || (tab === "ai" && aiExtracted)) && (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">

            {/* Insured Info */}
            <div className="px-6 py-5">
              <h3 className="font-heading font-semibold text-slate-700 mb-4">Insured Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: "insured_name",    label: "Insured Name *",  placeholder: "Acme Manufacturing LLC",   required: true,  testid: "insured-name-input" },
                  { key: "insured_email",   label: "Insured Email *", placeholder: "contact@insured.com",      required: true,  testid: "insured-email-input", type: "email" },
                  { key: "insured_contact", label: "Phone / Contact", placeholder: "555-0100",                 required: false, testid: "insured-contact-input" },
                  { key: "policy_number",   label: "Policy Number",   placeholder: "CP-2026-00001",            required: false, testid: "policy-number-input" },
                  { key: "broker_email",    label: "Broker Email (CC)", placeholder: "broker@agency.com",      required: false, testid: "broker-email-input", type: "email" },
                  { key: "broker_name",     label: "Broker Name",     placeholder: "Jane Smith",               required: false, testid: "broker-name-input" },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">{f.label}</label>
                    <input data-testid={f.testid} required={f.required} type={f.type || "text"}
                      value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors" />
                  </div>
                ))}
              </div>
            </div>

            {/* Property Address */}
            <div className="px-6 py-5">
              <h3 className="font-heading font-semibold text-slate-700 mb-4">Property Address</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Street Address *</label>
                  <input data-testid="address-input" required value={form.property_address}
                    onChange={e => setForm({ ...form, property_address: e.target.value })}
                    placeholder="1234 Main Street"
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">City</label>
                    <input data-testid="city-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                      placeholder="Dallas"
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">State</label>
                    <input data-testid="state-input" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
                      placeholder="TX"
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors" />
                  </div>
                </div>
              </div>
            </div>

            {/* Inspection Details */}
            <div className="px-6 py-5">
              <h3 className="font-heading font-semibold text-slate-700 mb-4">Inspection Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Due Date</label>
                  <input data-testid="due-date-input" type="date" value={form.due_date}
                    onChange={e => setForm({ ...form, due_date: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Notes</label>
                  <input data-testid="notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Optional notes"
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors" />
                </div>
              </div>

              {/* Business Type Profile */}
              {profiles.length > 0 && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">
                    Business Type Profile <span className="text-slate-400 font-normal">(optional — auto-selects coverage lines & eligibility checks)</span>
                  </label>
                  <select data-testid="business-profile-select" value={profileId}
                    onChange={e => {
                      const id = e.target.value;
                      setProfileId(id);
                      const p = profiles.find(x => x.id === id);
                      if (p?.inspection_types?.length) setSelectedTypes(p.inspection_types);
                    }}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors">
                    <option value="">No profile — select coverage lines manually</option>
                    {profiles.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.eligibility_criteria?.length ? ` — ${p.eligibility_criteria.length} eligibility checks` : ""}
                      </option>
                    ))}
                  </select>
                  {profileId && (() => {
                    const p = profiles.find(x => x.id === profileId);
                    return p?.eligibility_criteria?.length ? (
                      <p className="text-xs text-blue-600 mt-2 font-medium" data-testid="profile-eligibility-hint">
                        AI will assess {p.eligibility_criteria.length} eligibility requirement{p.eligibility_criteria.length !== 1 ? "s" : ""} for "{p.name}" during the survey and flag results on the review screen.
                      </p>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Inspection Types — checkboxes */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Inspection Types * <span className="text-slate-400 font-normal">(select all that apply — each counts as 1 survey)</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
                  {INSPECTION_TYPES.map(type => {
                    const active = selectedTypes.includes(type.value);
                    return (
                      <label key={type.value} data-testid={`type-${type.value}`}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all duration-150 ${active ? "border-navy bg-navy/5" : "border-slate-200 hover:border-slate-300"}`}>
                        <input type="checkbox" checked={active} onChange={() => toggleType(type.value)} className="mt-0.5 accent-navy" />
                        <div>
                          <div className={`text-sm font-medium ${active ? "text-navy" : "text-slate-700"}`}>{type.label}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{type.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {selectedTypes.length > 1 && (
                  <p className="text-xs text-blue-600 mt-2 font-medium">
                    {selectedTypes.length} types selected — insured will receive one hub link with all {selectedTypes.length} surveys. Each counts as 1 survey toward your plan.
                  </p>
                )}
              </div>

              {/* Survey Mode */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-3">Survey Mode</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: "ai_chat", icon: MessageSquare, title: "Mode 1 — AI Chat", desc: "Guided conversational survey with AI questions and photo analysis" },
                    { key: "form",    icon: FileText,      title: "Mode 2 — Structured Form", desc: "Insured fills out a questionnaire" },
                  ].map(m => (
                    <label key={m.key} data-testid={`mode-${m.key}`}
                      className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${form.mode === m.key ? "border-navy bg-navy/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <input type="radio" name="mode" value={m.key} checked={form.mode === m.key}
                        onChange={() => setForm({ ...form, mode: m.key, template_id: "" })} className="mt-0.5" />
                      <div>
                        <div className={`text-sm font-semibold flex items-center gap-1.5 ${form.mode === m.key ? "text-navy" : "text-slate-700"}`}>
                          <m.icon className="w-3.5 h-3.5" /> {m.title}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{m.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {form.mode === "form" && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Select Template</label>
                    <select data-testid="template-select" value={form.template_id}
                      onChange={e => setForm({ ...form, template_id: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white">
                      <option value="">-- Choose a template --</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name} ({t.questions?.length || 0} questions)</option>
                      ))}
                    </select>
                    <FormImporter onImported={t => { setTemplates(ts => [t, ...ts]); setForm(f => ({ ...f, template_id: t.id })); }} />
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3">
              <button type="button" onClick={() => navigate("/dashboard")}
                className="px-5 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <button data-testid="submit-order-btn" type="submit" disabled={loading || selectedTypes.length === 0}
                className="px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors disabled:opacity-60 flex items-center gap-2">
                {loading
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Creating...</>
                  : <><ClipboardList className="w-4 h-4" /> {selectedTypes.length > 1 ? `Create ${selectedTypes.length} Surveys` : "Create & Get Link"}</>}
              </button>
            </div>
          </form>
        )}

        {/* ── CSV TAB ── */}
        {tab === "csv" && (
          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="px-6 py-5">
              <h3 className="font-heading font-semibold text-slate-700 mb-2">Bulk CSV Upload</h3>
              <p className="text-sm text-slate-500 mb-4">Required columns: <code className="bg-slate-100 px-1 rounded text-xs">insured_name, insured_email, property_address</code></p>
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-4 text-xs text-slate-500 font-mono mb-4">
                insured_name,insured_email,property_address,city,state,policy_number<br/>
                Acme LLC,contact@acme.com,123 Main St,Dallas,TX,CP-001
              </div>
              <form onSubmit={handleCsvUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Default Inspection Type</label>
                  <select data-testid="csv-type-select" value={csvType} onChange={e => setCsvType(e.target.value)}
                    className="h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white w-full">
                    {INSPECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">CSV File *</label>
                  <input data-testid="csv-file-input" type="file" accept=".csv" onChange={e => setCsvFile(e.target.files[0])}
                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-navy file:text-white hover:file:bg-navy/90" />
                </div>
                <button data-testid="csv-upload-btn" type="submit" disabled={loading || !csvFile}
                  className="flex items-center gap-2 px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors disabled:opacity-60">
                  <Upload className="w-4 h-4" /> {loading ? "Uploading..." : "Upload & Create Inspections"}
                </button>
              </form>
              {csvResult && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="csv-result">
                  <p className="text-green-700 font-medium text-sm">{csvResult.created} inspections created successfully</p>
                  {csvResult.errors?.length > 0 && <p className="text-red-600 text-sm mt-1">{csvResult.errors.length} errors</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
